
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import './styles.scss'

import Sidebar from "./Sidebar"
import Body from "./Body"
import Header from "./Header"
import { SidebarProvider, useSidebar } from "./Sidebar/SidebarContext"
import { ScreenContextBridge, useScreen } from "./ScreenContext"
import { ChatPanelProvider, useChatPanel } from "./ChatPanel/ChatPanelContext"
import ChatPanel from "./ChatPanel"
import ChatFloatingButton from "./ChatPanel/FloatingButton"

import { useAuth } from '@contexts/AuthContext'
import { useCommunity } from 'contexts/community'


// Reads all loading sources and syncs their OR into ScreenContext.
// Keeps loading logic centralized — add new sources here as needed.
function ScreenLoadingBridge() {
    const { loading: authLoading } = useAuth()
    const { loading: communityLoading } = useCommunity()
    const { setLoading, bodyRef, sidebarRef } = useScreen()
    const location = useLocation()
    const routeTimerRef = useRef(null)

    // Auth + community loading sources
    useEffect(() => {
        setLoading(authLoading || communityLoading)
    }, [authLoading, communityLoading, setLoading])

    // Route-change loading: flash the animation on every navigation
    const loadingRef = useRef({ authLoading, communityLoading })
    useEffect(() => {
        loadingRef.current = { authLoading, communityLoading }
    }, [authLoading, communityLoading])

    // Only trigger on top-level route changes (first 2 segments).
    // e.g. /community/settings/general → /community/settings/integrations
    // produces the same key ("community/settings") → no animation.
    // But /community/settings → /other-community → different key → animation fires.
    // Only animate on top-level route changes (first segment only).
    // e.g. /banking/charges → /banking/subscriptions shares the same key
    // ("banking") → no animation. But /banking → /dashboard → different key.
    const topLevelKey = location.pathname.split('/').slice(0, 2).join('/')

    useEffect(() => {
        clearTimeout(routeTimerRef.current)
        setLoading(true)
        bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        routeTimerRef.current = setTimeout(() => {
            const { authLoading, communityLoading } = loadingRef.current
            if (!authLoading && !communityLoading) setLoading(false)
        }, 600)

        return () => clearTimeout(routeTimerRef.current)
    }, [topLevelKey])

    return null
}


// Two-layer cross-fade background effect. Layer A and B alternate as the
// "incoming" slot so both opacity transitions fire simultaneously.
function BackgroundEffect() {
    const { backgroundEffect } = useScreen()
    const [layers, setLayers] = useState([null, null])
    const [activeIdx, setActiveIdx] = useState(0)
    const prevEffect = useRef(undefined)

    useEffect(() => {
        if (backgroundEffect === prevEffect.current) return
        prevEffect.current = backgroundEffect

        const incomingIdx = activeIdx === 0 ? 1 : 0
        setLayers(prev => {
            const next = [...prev]
            next[incomingIdx] = backgroundEffect
            return next
        })
        setActiveIdx(incomingIdx)
    }, [backgroundEffect])

    return (
        <div className="cs-background-effect">
            {layers.map((src, i) => (
                <div
                    key={i}
                    className="cs-background-layer"
                    style={{
                        opacity: i === activeIdx && src ? 1 : 0,
                        backgroundImage: src ? `url(${src})` : 'none',
                    }}
                />
            ))}
        </div>
    )
}


// Auto-close the sidebar on route change when in mobile mode.
function MobileSidebarRouteCollapse() {
    const { isMobile, collapsed, setCollapsed } = useSidebar()
    const { pathname } = useLocation()

    useEffect(() => {
        if (isMobile && !collapsed) setCollapsed(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    return null
}

// On mobile, close the sidebar whenever the chat panel opens so the panel
// can take over the full screen without the sidebar overlapping it.
function MobileChatSidebarBridge() {
    const { isMobile, collapsed, setCollapsed } = useSidebar()
    const { open: chatOpen } = useChatPanel()

    useEffect(() => {
        if (isMobile && chatOpen && !collapsed) setCollapsed(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatOpen, isMobile])

    return null
}

export default function Screen({ children }) {
    return (
        <SidebarProvider>
            <ScreenContextBridge>
                <ChatPanelProvider>
                    <ScreenLoadingBridge />
                    <MobileSidebarRouteCollapse />
                    <MobileChatSidebarBridge />
                    <BackgroundEffect />
                    <div className="custom-screen">
                        <Sidebar />
                        <div className="cs-container">
                            <Header />
                            <Body>
                                {children}
                            </Body>
                        </div>
                        <ChatPanel />
                    </div>
                    <ChatFloatingButton />
                </ChatPanelProvider>
            </ScreenContextBridge>
        </SidebarProvider>
    )
} 